import * as React from 'react';
import {
    Autocomplete,
    Box,
    Checkbox,
    Chip,
    FormControlLabel,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    IconButton,
    Popper,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import BoltIcon from '@mui/icons-material/Bolt';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { IconCircleMinus, IconCirclePlus } from '@tabler/icons';
import { useDispatch, useSelector } from 'react-redux';
import { useQueryClient } from 'react-query';

import { selectCustomersidname } from '../../store/customers/customersSlice';
import { selectUser } from '../../store/user/userSlice';
import { subStringMatch } from '../../utils/sort';
import { SNACKBAR_OPEN } from '../../store/actions';
import { CUSTOMER_DOCUMENTS } from '../../hooks/customer/documents.hooks';
import { collect, createRealCustomerDocuments } from '../../api/customer/documents.api';
import getCustomerProductsPrices from '../../api/customer/products_prices.api';
import JSDB, { NKTypeConvertor } from '../../fromKotlin/nk';
import { dateObjectToString, roundDecimals } from '../../fromKotlin/nkutils';
import { createDocument } from '../../types/DocumentsTypes/utils';
import NoNegative, { WithNegative } from '../textFieldNumber';
import DocumentPreview from '../../views/customer/profile/documents/DocumentPreview';
import { handleKeyDown as handleTableArrowKeys } from '../../utils/tableAsKeyBorad';
import IItem, { hasSecondUnit, secondUnitIndex } from '../../types/product_delivery';
import type { DocumentType, ExtendedDocument } from '../../types/DocumentsTypes/Document';
import type { HandLine } from '../../types/DocumentsTypes/TaxNote';
import type { RootState } from '../../store';

const styles = () => ({
    popper: {
        maxWidth: 'fit-content'
    }
});
const PopperMy = function (props) {
    return <Popper {...props} style={styles.popper} placement="bottom-start" />;
};

type QuickDocType = 'delivery_certificate' | 'order' | 'invoice';

const QUICK_DOC_TYPES: { value: QuickDocType; label: string }[] = [
    { value: 'delivery_certificate', label: 'תעודת משלוח' },
    { value: 'order', label: 'הזמנה' },
    { value: 'invoice', label: 'חשבונית' }
];

const QUICK_DOC_PREFS_KEY = 'quickDocPrefs';

function parsePrefDate(value: unknown): Date {
    if (typeof value === 'string') {
        const [y, m, d] = value.split('-').map(Number);
        if (y && m && d) return new Date(y, m - 1, d);
    }
    return new Date();
}

function loadQuickDocPrefs(): { docType: QuickDocType; date: Date } {
    try {
        const raw = localStorage.getItem(QUICK_DOC_PREFS_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            const docType = QUICK_DOC_TYPES.some((t) => t.value === parsed.docType)
                ? (parsed.docType as QuickDocType)
                : 'delivery_certificate';
            return { docType, date: parsePrefDate(parsed.date) };
        }
    } catch {
        /* ignore */
    }
    return { docType: 'delivery_certificate', date: new Date() };
}

function saveQuickDocPrefs(docType: QuickDocType, date: Date) {
    try {
        localStorage.setItem(QUICK_DOC_PREFS_KEY, JSON.stringify({ docType, date: dateObjectToString(date) }));
    } catch {
        /* ignore */
    }
}

type QuickLine = IItem;

interface CreatedDocEntry {
    key: string;
    doc: ExtendedDocument;
    clientName: string;
    total: number;
}

const EMPTY_DETAILS = { city: '', companyId: '', companyName: '', email: '', id: 0, phone: '', street: '', streetNumber: '' };

/**
 * Identity frozen onto a sequential invoice. AUTO follows the network mode:
 * split=2 (מפוצל עם ח.פ עצמאי) with a real own ח.פ bills the branch, otherwise the network.
 */
function invoiceDetailsFor(client: { id: number; email?: string; name?: string }) {
    try {
        const nk = JSDB()?.getClient(Number(client.id))?.first;
        const resolved = nk?.buildStaticData?.(undefined, 'AUTO');
        const companyId = String(resolved?.business_id ?? '').trim();
        if (!companyId) return { ...EMPTY_DETAILS };
        return {
            city: resolved.city || '',
            companyId,
            companyName: resolved.business_name || client.name || '',
            email: client.email ?? '',
            id: Number(resolved.id ?? client.id),
            phone: resolved.phone || '',
            street: resolved.street || '',
            streetNumber: resolved.streetNumber || ''
        };
    } catch {
        return { ...EMPTY_DETAILS };
    }
}

function emptyHandLine(client?: any, user?: any, description = 'סכום ידני'): HandLine {
    return {
        value: 0,
        tax: 0,
        withoutMaam: user?.nkObject?.no_tax_p === 1 || !!client?.noTaxClient,
        includeTax: !!client?.includeTax,
        amount: 1,
        description
    };
}

function handLineHasValue(h: HandLine): boolean {
    return roundDecimals((h.value || 0) * (h.amount || 0), 4) !== 0;
}

/** Mirrors NewInvoice's collectValues for products + handArray, no-discount. */
function computeInvoiceTotals(
    products: IItem[],
    cid: number,
    dateStr: string,
    handArray: HandLine[] = [],
    includeTaxDefault = false
) {
    let productsTax = 0;
    let productsValue = 0;
    if (products.length) {
        const pds = NKTypeConvertor().toKotlinList(collect(products, cid, true, false));
        const result = JSDB().productDeliveryToAmount(pds, cid, dateStr, true);
        productsTax = roundDecimals(result.second, 4);
        productsValue = roundDecimals(result.third, 4);
    }

    let handValue = 0;
    let handTax = 0;
    handArray.forEach((item) => {
        const includeTax = item.includeTax ?? includeTaxDefault;
        handValue += item.value * item.amount + (!includeTax ? item.tax * item.amount : 0);
        handTax += item.tax * item.amount;
    });

    let totalTax = productsTax + handTax;
    const totalValue = productsValue + handValue;
    let totalBefore = roundDecimals(totalValue - totalTax, 4);
    let roundValue = 0;
    let roundTax = 0;
    const roundTaxNote = parseInt(localStorage.getItem('roundTax') ?? '0') === 1;
    if (roundTaxNote) {
        const out = JSDB().calculateRoundingTax(totalBefore, totalValue, 0, dateStr);
        roundTax = roundDecimals(out.second, 4);
        totalTax += roundTax;
        roundValue = roundDecimals(out.first, 4);
        totalBefore = roundDecimals(totalBefore + out.first - out.second, 4);
    }
    const preValue = totalBefore;
    const mainValue = roundDecimals(totalTax + totalBefore, 4);
    return { mainValue, preValue, taxValue: roundDecimals(totalTax, 4), roundValue, roundTax };
}

function calcHandTax(unitValue: number, includeTax: boolean, withoutMaam: boolean, dateStr: string): number {
    if (withoutMaam) return 0;
    if (includeTax) return unitValue - unitValue / JSDB().tax.get(dateStr);
    return unitValue * JSDB().tax.getPercentDivided(dateStr);
}

function lineQuantity(l: QuickLine): number {
    return (l.quantity || 0) - (l.quantityReturns || 0) + (l.quantitySecond || 0) * (l.conversion_ratio || 0);
}

function lineTotal(l: QuickLine): number {
    return roundDecimals(lineQuantity(l) * (l.totalPrice || 0), 2);
}

/** Second-unit name, plus conversion (`קרטון = 12 יח'`) when the product has a rate. */
function secondUnitCaption(row: IItem): string | null {
    if (!hasSecondUnit(row)) return null;
    const extra = row.available_units[secondUnitIndex(row)];
    const primary = row.available_units[0] || "יח'";
    const rate = Number(row.conversion_ratio) || 0;
    if (!extra) return null;
    if (rate > 0 && rate < 1) {
        const inverse = Math.round((1 / rate) * 1000) / 1000;
        return `${inverse} ${extra} = ${primary}`;
    }
    if (rate >= 1) return `${extra} = ${rate} ${primary}`;
    return extra;
}

function isQuickDocType(value: unknown): value is QuickDocType {
    return value === 'delivery_certificate' || value === 'order' || value === 'invoice';
}

function QuickDocDialog({
    open,
    onClose,
    initialClient,
    initialDocType
}: {
    open: boolean;
    onClose: () => void;
    initialClient?: any;
    initialDocType?: string;
}) {
    const dispatch = useDispatch();
    const cache = useQueryClient();
    const customers: any[] = useSelector(selectCustomersidname);
    const user = useSelector((state: RootState) => selectUser(state));
    const externalIdSearch = parseInt(localStorage.getItem('externalIdOrders') ?? '0') === 1;

    const initialPrefs = React.useMemo(() => loadQuickDocPrefs(), []);
    const [docType, setDocType] = React.useState<QuickDocType>(initialPrefs.docType);
    const [date, setDate] = React.useState<Date>(initialPrefs.date);
    const [client, setClient] = React.useState<any>(null);

    const [catalog, setCatalog] = React.useState<IItem[]>([]);
    const [catalogLoading, setCatalogLoading] = React.useState(false);

    const [lines, setLines] = React.useState<QuickLine[]>([]);
    const [handArray, setHandArray] = React.useState<HandLine[]>([emptyHandLine()]);
    const [productInput, setProductInput] = React.useState('');
    const [pendingFocusId, setPendingFocusId] = React.useState<number | null>(null);
    const [savePrice, setSavePrice] = React.useState<boolean | undefined>(undefined);

    const [creating, setCreating] = React.useState(false);
    const [createdDocs, setCreatedDocs] = React.useState<CreatedDocEntry[]>([]);
    const [previewDoc, setPreviewDoc] = React.useState<ExtendedDocument | null>(null);

    const clientInputRef = React.useRef<HTMLInputElement | null>(null);
    const productInputRef = React.useRef<HTMLInputElement | null>(null);
    const qtyRefs = React.useRef<Map<number, HTMLInputElement>>(new Map());
    const seededOpenRef = React.useRef(false);

    const showReturns = docType === 'delivery_certificate';
    const dateStr = dateObjectToString(date);
    const invoiceIdentity = React.useMemo(
        () => (docType === 'invoice' && client?.id ? invoiceDetailsFor(client) : null),
        [docType, client]
    );
    const allowNoTax = user?.nkObject?.no_tax_p === 1;
    const withDiscount = parseInt(localStorage.getItem('withDiscount') ?? '0') === 1;

    const resetHandArray = React.useCallback(
        (forClient?: any) => setHandArray([emptyHandLine(forClient ?? null, user)]),
        [user]
    );

    const resetAll = React.useCallback(() => {
        // Keep docType + date — user preference across creates/sessions.
        setClient(null);
        setLines([]);
        setCatalog([]);
        setProductInput('');
        setCreatedDocs([]);
        resetHandArray(null);
    }, [resetHandArray]);

    const handleClose = React.useCallback(() => {
        if (creating) return;
        onClose();
        resetAll();
        seededOpenRef.current = false;
    }, [creating, onClose, resetAll]);

    React.useEffect(() => {
        if (!open) {
            seededOpenRef.current = false;
            return;
        }
        if (seededOpenRef.current) return;
        seededOpenRef.current = true;
        if (isQuickDocType(initialDocType)) {
            setDocType(initialDocType);
        }
        if (initialClient) {
            setClient(initialClient);
            resetHandArray(initialClient);
            setTimeout(() => productInputRef.current?.focus(), 80);
        } else {
            setTimeout(() => clientInputRef.current?.focus(), 50);
        }
    }, [open, initialClient, initialDocType, resetHandArray]);

    React.useEffect(() => {
        saveQuickDocPrefs(docType, date);
    }, [docType, date]);

    const handleLineKeyDown = React.useCallback((e: React.KeyboardEvent) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            productInputRef.current?.focus();
            return;
        }
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight' || e.key === 'ArrowUp' || e.key === 'ArrowDown') {
            e.preventDefault();
            handleTableArrowKeys(e);
        }
    }, []);

    // Load client's product catalog whenever the client (or the document date) changes.
    React.useEffect(() => {
        if (!client) {
            setCatalog([]);
            return;
        }
        let cancelled = false;
        setCatalogLoading(true);
        getCustomerProductsPrices(String(client.id), dateObjectToString(date), true, undefined, false, true)
            .then((res) => {
                if (!cancelled) setCatalog(res || []);
            })
            .catch(() => {
                if (!cancelled) {
                    setCatalog([]);
                    dispatch({
                        type: SNACKBAR_OPEN,
                        open: true,
                        message: 'שגיאה בטעינת מחירון הלקוח',
                        variant: 'alert',
                        alertSeverity: 'error'
                    });
                }
            })
            .finally(() => {
                if (!cancelled) setCatalogLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client?.id, dateObjectToString(date)]);

    React.useEffect(() => {
        if (pendingFocusId == null) return;
        const el = qtyRefs.current.get(pendingFocusId);
        if (el) {
            el.focus();
            el.select();
        }
        setPendingFocusId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pendingFocusId]);

    const addProduct = React.useCallback((product: IItem) => {
        setLines((prev) => {
            const idx = prev.findIndex((l) => l.id === product.id);
            if (idx >= 0) return prev;
            return [...prev, { ...product, quantity: 0, quantityReturns: 0, quantitySecond: 0, quantitySecondReturns: 0 }];
        });
        setPendingFocusId(product.id);
        setProductInput('');
    }, []);

    const updateLine = React.useCallback((id: number, patch: Partial<QuickLine>) => {
        setLines((prev) =>
            prev.map((l) => {
                if (l.id !== id) return l;
                const next = { ...l, ...patch };
                // Same as NewDeliveryCertificate onProductChange: remember a typed unit price.
                if (patch.totalPrice !== undefined) {
                    if (patch.totalPrice !== l.totalPrice) next.priceTouched = true;
                    else if (l.priceTouched) next.priceTouched = true;
                }
                return next;
            })
        );
    }, []);

    const removeLine = React.useCallback((id: number) => {
        setLines((prev) => prev.filter((l) => l.id !== id));
        qtyRefs.current.delete(id);
    }, []);

    const productOptions = React.useMemo(() => catalog, [catalog]);

    const filterProductOptions = React.useCallback((options: IItem[], { inputValue }: { inputValue: string }) => {
        const q = inputValue.trim();
        if (!q) return options.slice(0, 50);
        return options
            .filter((o) => subStringMatch(q, [o.name, o.barcode, String(o.id), o.external_id]))
            .slice(0, 50);
    }, []);

    const filterClientOptions = React.useCallback((options, { inputValue }) => {
        return options
            .filter((option) => subStringMatch(inputValue, [option.name, externalIdSearch ? option.externalId : option.id]))
            .sort((a, b) => {
                const n1 = externalIdSearch ? a.externalId : a.id;
                const n2 = externalIdSearch ? b.externalId : b.id;
                if (Number(n1) && Number(n2)) return Number(n1) > Number(n2) ? 1 : -1;
                if (Number(n1)) return 1;
                if (Number(n2)) return -1;
                return n1 > n2 ? 1 : -1;
            })
            .slice(0, 100);
    }, [externalIdSearch]);

    const total = React.useMemo(() => {
        const productsTotal = lines.reduce((sum, l) => sum + lineTotal(l), 0);
        if (docType !== 'invoice') return productsTotal;
        const handTotal = handArray.reduce((sum, h) => {
            const includeTax = h.includeTax ?? !!client?.includeTax;
            return sum + h.value * h.amount + (!includeTax ? h.tax * h.amount : 0);
        }, 0);
        return roundDecimals(productsTotal + handTotal, 2);
    }, [lines, handArray, docType, client?.includeTax]);

    const buildProducts = React.useCallback(
        () =>
            lines.filter(
                (l) => (l.quantity || 0) > 0 || (l.quantityReturns || 0) > 0 || (l.quantitySecond || 0) > 0
            ),
        [lines]
    );

    const showSecondUnitCol = React.useMemo(() => lines.some((l) => hasSecondUnit(l)), [lines]);
    const emptyColSpan = 5 + (showReturns ? 1 : 0) + (showSecondUnitCol ? 1 : 0);

    const activeHandLines = React.useMemo(() => handArray.filter(handLineHasValue), [handArray]);
    const canCreate =
        Boolean(client) &&
        !creating &&
        (buildProducts().length > 0 || (docType === 'invoice' && activeHandLines.length > 0));

    const updateHandLine = React.useCallback((index: number, patch: Partial<HandLine>) => {
        setHandArray((prev) => prev.map((item, i) => (i === index ? { ...item, ...patch } : item)));
    }, []);

    const setHandUnitValue = React.useCallback(
        (index: number, unitValue: number, overrides?: Partial<HandLine>) => {
            setHandArray((prev) =>
                prev.map((item, i) => {
                    if (i !== index) return item;
                    const next = { ...item, ...overrides, value: unitValue };
                    const includeTax = next.includeTax ?? !!client?.includeTax;
                    next.tax = calcHandTax(unitValue, includeTax, !!next.withoutMaam, dateStr);
                    return next;
                })
            );
        },
        [client?.includeTax, dateStr]
    );

    const handleCreate = React.useCallback(
        async (continueAfter: boolean) => {
            if (!client) return;
            const productsForDoc = buildProducts();
            const handForDoc = docType === 'invoice' ? activeHandLines : [];
            if (productsForDoc.length === 0 && handForDoc.length === 0) return;
            setCreating(true);
            try {
                let doc: ExtendedDocument;
                if (docType === 'invoice') {
                    const totals = computeInvoiceTotals(
                        productsForDoc,
                        Number(client.id),
                        dateStr,
                        handForDoc,
                        !!client.includeTax
                    );
                    doc = createDocument({
                        id: 0,
                        entId: String(client.id),
                        entName: client.name,
                        entBName: client.name,
                        date,
                        issueDate: new Date(),
                        type: 'invoice',
                        status: 'approved',
                        products: productsForDoc,
                        comment: '',
                        mainValue: totals.mainValue,
                        preValue: totals.preValue,
                        taxValue: totals.taxValue,
                        round: totals.roundValue,
                        round_tax: totals.roundTax,
                        hand: 0,
                        hand_tax: 0,
                        handArray: handForDoc,
                        discount: 0,
                        payments: [],
                        deliveryCertificatesIds: [],
                        details: invoiceDetailsFor(client),
                        agent: JSDB().currentAgent
                    });
                } else {
                    const mainValue = roundDecimals(
                        productsForDoc.reduce((sum, p) => sum + lineTotal(p), 0),
                        4
                    );
                    doc = createDocument({
                        id: 0,
                        entId: String(client.id),
                        entName: client.name,
                        entBName: client.name,
                        date,
                        issueDate: new Date(),
                        type: docType as DocumentType,
                        status: 'open',
                        products: productsForDoc,
                        comment: '',
                        mainValue,
                        preValue: mainValue,
                        taxValue: 0,
                        agent: JSDB().currentAgent
                    });
                }

                // Same as NewDeliveryCertificate: only stamp prices on this note when the
                // "שמירת מחיר רק בתעודה זו" flag is on. Otherwise company savePriceInNote() applies.
                if (savePrice) (doc as any).savePrice = savePrice;

                const created = await createRealCustomerDocuments(String(client.id), doc);
                cache.invalidateQueries(CUSTOMER_DOCUMENTS);
                const createdDoc = (Array.isArray(created) ? created[0] : created) as ExtendedDocument;
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'המסמך נוצר בהצלחה!',
                    variant: 'alert',
                    alertSeverity: 'success'
                });
                setCreatedDocs((prev) => [
                    { key: `${createdDoc?.id ?? Date.now()}-${prev.length}`, doc: createdDoc, clientName: client.name, total: doc.mainValue },
                    ...prev
                ].slice(0, 20));

                if (continueAfter) {
                    setLines([]);
                    setClient(null);
                    setCatalog([]);
                    setProductInput('');
                    resetHandArray(null);
                    setTimeout(() => clientInputRef.current?.focus(), 50);
                } else {
                    onClose();
                    resetAll();
                }
            } catch (e) {
                console.log(e);
                dispatch({
                    type: SNACKBAR_OPEN,
                    open: true,
                    message: 'אירעה שגיאה ביצירת המסמך',
                    variant: 'alert',
                    alertSeverity: 'error'
                });
            } finally {
                setCreating(false);
            }
        },
        [client, date, dateStr, docType, buildProducts, activeHandLines, dispatch, cache, resetAll, resetHandArray, onClose, savePrice]
    );

    const onDialogKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) {
                e.preventDefault();
                if (canCreate) void handleCreate(false);
            }
        },
        [canCreate, handleCreate]
    );

    return (
        <>
            {open ? (
                <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                    <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <BoltIcon sx={{ color: '#ff6f00' }} />
                        הוצאת כמה מסמכים ברצף
                        <Box sx={{ flexGrow: 1 }} />
                        <IconButton size="small" onClick={handleClose}>
                            <CloseIcon fontSize="small" />
                        </IconButton>
                    </DialogTitle>
                    <DialogContent dividers onKeyDown={onDialogKeyDown}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5, alignItems: 'flex-start', mb: 2 }}>
                            <ToggleButtonGroup
                                size="small"
                                exclusive
                                value={docType}
                                onChange={(e, value) => {
                                    if (value) setDocType(value);
                                }}
                            >
                                {QUICK_DOC_TYPES.map((t) => (
                                    <ToggleButton key={t.value} value={t.value}>
                                        {t.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>

                            <TextField
                                type="date"
                                size="small"
                                label="תאריך"
                                value={dateObjectToString(date)}
                                onChange={(e) => {
                                    const [y, m, d] = e.target.value.split('-').map(Number);
                                    if (y && m && d) setDate(new Date(y, m - 1, d));
                                }}
                                InputLabelProps={{ shrink: true }}
                                sx={{ width: 170 }}
                            />

                            <Autocomplete
                                value={client}
                                onChange={(e, value) => {
                                    setClient(value);
                                    resetHandArray(value);
                                    if (value) {
                                        setTimeout(() => productInputRef.current?.focus(), 50);
                                    }
                                }}
                                PopperComponent={PopperMy}
                                options={customers}
                                getOptionLabel={(o: any) => `${externalIdSearch ? o.externalId : o.id} - ${o.name}`}
                                filterOptions={filterClientOptions}
                                size="small"
                                autoHighlight
                                sx={{ minWidth: 260, flexGrow: 1 }}
                                renderInput={(params) => (
                                    <TextField {...params} inputRef={clientInputRef} label="חפש לקוח.." autoFocus fullWidth size="small" />
                                )}
                            />
                            {invoiceIdentity?.companyId ? (
                                <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>
                                    ח.פ {invoiceIdentity.companyId}
                                    {invoiceIdentity.companyName ? ` · ${invoiceIdentity.companyName}` : ''}
                                </Typography>
                            ) : null}
                        </Box>

                        <Autocomplete
                            value={null}
                            inputValue={productInput}
                            onInputChange={(e, value, reason) => {
                                if (reason !== 'reset') setProductInput(value);
                            }}
                            onChange={(e, value: IItem | null) => {
                                if (value) addProduct(value);
                            }}
                            options={productOptions}
                            disabled={!client}
                            loading={catalogLoading}
                            getOptionLabel={(o: IItem) => `${o.id} - ${o.name}`}
                            filterOptions={filterProductOptions}
                            autoHighlight
                            openOnFocus
                            renderOption={(props, option: IItem) => (
                                <li {...props} key={option.id}>
                                    <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between', gap: 2 }}>
                                        <span>
                                            {option.id} - {option.name}
                                            {hasSecondUnit(option) && secondUnitCaption(option) ? (
                                                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                                                    ({secondUnitCaption(option)})
                                                </Typography>
                                            ) : null}
                                        </span>
                                        <Typography variant="caption" color="text.secondary">
                                            ₪{roundDecimals(option.totalPrice || 0, 2)}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    inputRef={productInputRef}
                                    label={client ? (catalogLoading ? 'טוען מחירון…' : 'הוסף מוצר..') : 'בחר לקוח כדי להוסיף מוצרים'}
                                    size="small"
                                    fullWidth
                                />
                            )}
                        />

                        <Table size="small" sx={{ mt: 2 }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>מוצר</TableCell>
                                    {showSecondUnitCol ? <TableCell align="center" width={130}>יחידה שנייה</TableCell> : null}
                                    <TableCell align="center" width={110}>כמות</TableCell>
                                    {showReturns ? <TableCell align="center" width={110}>החזרות</TableCell> : null}
                                    <TableCell align="center" width={130}>מחיר יח'</TableCell>
                                    <TableCell align="center" width={110}>סה"כ</TableCell>
                                    <TableCell align="center" width={40} />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {lines.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={emptyColSpan}>
                                            <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 2 }}>
                                                {client ? 'חפש והוסף מוצרים כדי להתחיל' : 'בחר לקוח כדי להתחיל'}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    lines.map((l) => (
                                        <TableRow key={l.id} hover>
                                            <TableCell>
                                                {l.name}
                                                {hasSecondUnit(l) && (Number(l.conversion_ratio) || 0) > 0 ? (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        {secondUnitCaption(l)}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            {showSecondUnitCol ? (
                                                <TableCell align="center" sx={{ p: 0.5 }}>
                                                    {hasSecondUnit(l) ? (
                                                        <>
                                                            <NoNegative
                                                                value={l.quantitySecond}
                                                                size="small"
                                                                style={{ width: 90 }}
                                                                inputProps={{ style: { textAlign: 'center' } }}
                                                                changer={(v: number) => updateLine(l.id, { quantitySecond: v })}
                                                                onKeyDown={handleLineKeyDown}
                                                            />
                                                            <Typography variant="caption" display="block" color="text.secondary">
                                                                {l.available_units[secondUnitIndex(l)]}
                                                            </Typography>
                                                        </>
                                                    ) : null}
                                                </TableCell>
                                            ) : null}
                                            <TableCell align="center" sx={{ p: 0.5 }}>
                                                <NoNegative
                                                    value={l.quantity}
                                                    size="small"
                                                    style={{ width: 90 }}
                                                    inputProps={{ style: { textAlign: 'center' } }}
                                                    inputRef={(el: HTMLInputElement | null) => {
                                                        if (el) qtyRefs.current.set(l.id, el);
                                                        else qtyRefs.current.delete(l.id);
                                                    }}
                                                    changer={(v: number) => updateLine(l.id, { quantity: v })}
                                                    onKeyDown={handleLineKeyDown}
                                                />
                                                {hasSecondUnit(l) && l.available_units?.[0] ? (
                                                    <Typography variant="caption" display="block" color="text.secondary">
                                                        {l.available_units[0]}
                                                    </Typography>
                                                ) : null}
                                            </TableCell>
                                            {showReturns ? (
                                                <TableCell align="center" sx={{ p: 0.5 }}>
                                                    <NoNegative
                                                        value={l.quantityReturns}
                                                        size="small"
                                                        style={{ width: 90 }}
                                                        inputProps={{ style: { textAlign: 'center' } }}
                                                        changer={(v: number) => updateLine(l.id, { quantityReturns: v })}
                                                        onKeyDown={handleLineKeyDown}
                                                    />
                                                </TableCell>
                                            ) : null}
                                            <TableCell align="center" sx={{ p: 0.5 }}>
                                                <NoNegative
                                                    value={l.totalPrice}
                                                    size="small"
                                                    style={{ width: 100, direction: 'ltr' }}
                                                    inputProps={{ style: { textAlign: 'center' } }}
                                                    changer={(v: number) => updateLine(l.id, { totalPrice: v })}
                                                    onKeyDown={handleLineKeyDown}
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                                                    ₪{lineTotal(l)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton size="small" onClick={() => removeLine(l.id)}>
                                                    <DeleteOutlineIcon fontSize="small" color="error" />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>

                        {docType === 'invoice' && client ? (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                                    סכום עם תיאור ידני
                                </Typography>
                                {handArray.map((item, i) => {
                                    const includeTax = item.includeTax ?? !!client.includeTax;
                                    return (
                                        <Box
                                            key={i}
                                            sx={{
                                                display: 'flex',
                                                flexWrap: 'wrap',
                                                gap: 1,
                                                alignItems: 'center',
                                                mb: 1
                                            }}
                                        >
                                            <TextField
                                                size="small"
                                                label="תיאור ידני"
                                                value={item.description}
                                                onChange={(e) => updateHandLine(i, { description: e.target.value })}
                                                sx={{ minWidth: 160, flexGrow: 1 }}
                                            />
                                            <WithNegative
                                                size="small"
                                                label="כמות"
                                                value={item.amount}
                                                style={{ width: 90 }}
                                                changer={(v: number) => updateHandLine(i, { amount: Number(v) })}
                                            />
                                            <NoNegative
                                                size="small"
                                                label={`מחיר${includeTax ? '(כולל)' : ''}`}
                                                value={item.value}
                                                style={{ width: 110 }}
                                                changer={(v: number) => setHandUnitValue(i, Number(v))}
                                            />
                                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                <Typography variant="caption">כולל מע״מ</Typography>
                                                <Checkbox
                                                    size="small"
                                                    checked={includeTax}
                                                    disabled={!!item.withoutMaam}
                                                    onChange={(e) => setHandUnitValue(i, Number(item.value), { includeTax: e.target.checked })}
                                                    color="primary"
                                                    sx={{ p: 0.5 }}
                                                />
                                            </Box>
                                            {allowNoTax ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <Typography variant="caption">ללא מעמ</Typography>
                                                    <Checkbox
                                                        size="small"
                                                        checked={!!item.withoutMaam}
                                                        onChange={(e) =>
                                                            setHandUnitValue(i, Number(item.value), { withoutMaam: e.target.checked })
                                                        }
                                                        color="primary"
                                                        sx={{ p: 0.5 }}
                                                    />
                                                </Box>
                                            ) : null}
                                            <WithNegative
                                                size="small"
                                                label={`סהכ${includeTax ? '(כולל)' : ''}`}
                                                value={roundDecimals(item.value * item.amount, 3) || 0}
                                                style={{ width: 110 }}
                                                changer={(v: number) => {
                                                    const amount = item.amount;
                                                    if (!amount) return;
                                                    setHandUnitValue(i, roundDecimals(Number(v) / amount, 4));
                                                }}
                                            />
                                            {i !== 0 ? (
                                                <Tooltip title="מחיקת שורה ידנית">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => setHandArray((prev) => prev.filter((_, idx) => idx !== i))}
                                                    >
                                                        <IconCircleMinus size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                            {i === handArray.length - 1 ? (
                                                <Tooltip title="הוספת שורה ידנית">
                                                    <IconButton
                                                        size="small"
                                                        onClick={() =>
                                                            setHandArray((prev) => [...prev, emptyHandLine(client, user, '')])
                                                        }
                                                    >
                                                        <IconCirclePlus size={18} />
                                                    </IconButton>
                                                </Tooltip>
                                            ) : null}
                                        </Box>
                                    );
                                })}
                            </Box>
                        ) : null}

                        {withDiscount && docType === 'delivery_certificate' ? (
                            <FormControlLabel
                                sx={{ mt: 1 }}
                                control={
                                    <Checkbox
                                        checked={!!savePrice}
                                        onChange={(e) => setSavePrice(e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="שמירת מחיר רק בתעודה זו"
                            />
                        ) : null}

                        {lines.length > 0 || (docType === 'invoice' && activeHandLines.length > 0) ? (
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                                <Typography variant="h4">סה"כ: ₪{roundDecimals(total, 2)}</Typography>
                            </Box>
                        ) : null}

                        {createdDocs.length > 0 ? (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    נוצרו בסשן הזה:
                                </Typography>
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {createdDocs.map((c) => (
                                        <Chip
                                            key={c.key}
                                            size="small"
                                            icon={<VisibilityOutlinedIcon fontSize="small" />}
                                            label={`${c.doc?.name ?? ''} ${c.doc?.id ?? ''} · ${c.clientName} · ₪${roundDecimals(c.total, 2)}`}
                                            onClick={() => setPreviewDoc(c.doc)}
                                            variant="outlined"
                                        />
                                    ))}
                                </Box>
                            </Box>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Typography variant="caption" color="text.secondary" sx={{ mr: 'auto', pl: 1 }}>
                            Ctrl+Enter ליצירה · ←→ בין שדות · Esc לסגירה
                        </Typography>
                        <Button onClick={handleClose} disabled={creating}>
                            ביטול
                        </Button>
                        <Button onClick={() => handleCreate(true)} disabled={!canCreate} variant="outlined">
                            צור והמשך
                        </Button>
                        <Button onClick={() => handleCreate(false)} disabled={!canCreate} variant="contained" color="success">
                            צור מסמך
                        </Button>
                    </DialogActions>
                </Dialog>
            ) : null}

            {previewDoc ? (
                <Dialog fullWidth maxWidth="lg" open onClose={() => setPreviewDoc(null)} sx={{ '& .MuiDialog-paper': { p: 0 } }}>
                    <DocumentPreview document={previewDoc} creditRecepitValue={false} setCreditRecepitValue={() => undefined} />
                </Dialog>
            ) : null}
        </>
    );
}

export default React.memo(QuickDocDialog);
